import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    project: '',
    rating: 5,
    comment: ''
  });

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load reviews:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/reviews`, formData);
      alert('Thank you for your review! It will be published after approval.');
      setFormData({
        name: '',
        email: '',
        project: '',
        rating: 5,
        comment: ''
      });
    } catch (error) {
      console.error('Failed to submit review:', error);
      alert('Failed to submit review. Please try again.');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Client <span className="text-[#D4AF37]">Reviews</span>
          </h1>
          <div className="w-20 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            See what our satisfied clients have to say about working with BuildBroz
          </p>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center text-white">Loading reviews...</div>
          ) : reviews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
              {reviews.map((review) => (
                <Card key={review.id} className="review-card bg-[#1A1A1A] border-gray-800 p-8">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} size={24} className="text-[#D4AF37] fill-[#D4AF37]" />
                      ))}
                    </div>
                    <span className="text-sm text-gray-500">{new Date(review.date).toLocaleDateString()}</span>
                  </div>
                  <p className="text-gray-300 mb-6 italic text-lg leading-relaxed">
                    "{review.comment}"
                  </p>
                  <div className="border-t border-gray-700 pt-4">
                    <p className="font-semibold text-white text-lg">{review.name}</p>
                    <p className="text-[#D4AF37] mb-1">{review.project}</p>
                    <p className="text-sm text-gray-500">{review.location}</p>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-400 mb-20">
              <p>No reviews yet. Be the first to share your experience!</p>
            </div>
          )}

        {/* Submit Review Form */}
          <div className="max-w-3xl mx-auto">
            <Card className="bg-[#1A1A1A] border-gray-800 p-8">
              <h2 className="text-3xl font-bold text-white mb-6 text-center">
                Share Your <span className="text-[#D4AF37]">Experience</span>
              </h2>
              <div className="w-20 h-1 bg-[#D4AF37] mx-auto mb-8"></div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-white mb-2 block font-medium">Your Name</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                    />
                  </div>
                  <div>
                    <label className="text-white mb-2 block font-medium">Email</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white mb-2 block font-medium">Project Type</label>
                  <Input
                    name="project"
                    value={formData.project}
                    onChange={handleChange}
                    placeholder="e.g., Custom Home Build, Renovation"
                    required
                    className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                  />
                </div>

                <div>
                  <label className="text-white mb-2 block font-medium">Rating</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => setFormData({ ...formData, rating })}
                        className="transition-transform hover:scale-110"
                      >
                        <Star
                          size={32}
                          className={rating <= formData.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-gray-600'}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-white mb-2 block font-medium">Your Review</label>
                  <Textarea
                    name="comment"
                    value={formData.comment}
                    onChange={handleChange}
                    placeholder="Share your experience working with BuildBroz..."
                    rows={6}
                    required
                    className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold text-lg py-6"
                >
                  Submit Review
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;